import { Navigate, useRouter } from '@tanstack/react-router';
import { AppErrorBoundary } from '@/components/app-error-boundary';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { type ProductTaxonomyVariant, useProductTaxonomy } from '@/services/product-taxonomy';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import type { Delivery } from '@/services/db/delivery/entity';
import { getDeliveryState } from '@/services/db/delivery/utils';
import { normalizeWorkspaceIdRouteParam, type WorkspaceIdRouteParamInput } from '@/services/router/workspace-id-param';
import Journeys from '@/pages/journeys';
import { DeliveryDetailPage } from './delivery-detail';
import OutboxPage from './index.page';
import type { OutboxTab } from './types';

/** The Drafts tab's URL segment — unchanged across both taxonomy variants. */
const DRAFTS_TAB = 'drafts';

/**
 * The Outbox's taxonomy URL segments, each tagged with the taxonomy `variant` it's canonical
 * for, its "Sent"-tab name, and its splat route. Everything else (the `OutboxSegment` type, the
 * variant→segment lookup) derives from this catalog, so the segment set is defined in one place.
 */
const OUTBOX_SEGMENTS = {
  outbox: { variant: 'current', sentTab: 'deliveries', splatRoute: '/workspaces/$workspaceId/journeys/outbox/$' },
  message_activity: {
    variant: 'future',
    sentTab: 'sent',
    splatRoute: '/workspaces/$workspaceId/journeys/message_activity/$',
  },
} as const satisfies Record<string, { variant: ProductTaxonomyVariant; sentTab: string; splatRoute: string }>;

export type OutboxSegment = keyof typeof OUTBOX_SEGMENTS;

/** Canonical segment for each taxonomy variant — the reverse of each entry's `variant`. */
const SEGMENT_BY_VARIANT = Object.fromEntries(
  (Object.keys(OUTBOX_SEGMENTS) as OutboxSegment[]).map((segment) => [OUTBOX_SEGMENTS[segment].variant, segment]),
) as Record<ProductTaxonomyVariant, OutboxSegment>;

/** The URL segment for a logical tab under a given taxonomy segment. */
function tabSegmentFor(segment: OutboxSegment, tab: OutboxTab): string {
  return tab === 'drafts' ? DRAFTS_TAB : OUTBOX_SEGMENTS[segment].sentTab;
}

/** What React renders for a splat path: the tab list, a single detail, or an Ember hand-off. */
type OutboxView = { kind: 'ember' } | { kind: 'list'; tab: OutboxTab } | { kind: 'detail'; tab: OutboxTab; id: string };

/**
 * Classify the splat path. React owns the Deliveries/Sent + Drafts tab lists and the
 * Deliveries/Sent `:id` detail; the index redirect, draft detail/edit, and any deeper path
 * hand off to the embedded Ember host.
 */
function classifyOutboxPath(segment: OutboxSegment, splat: string): OutboxView {
  const pathSegments = splat.split('/').filter((part) => part.length > 0);
  const rest = pathSegments.slice(1);
  const tab: OutboxTab | null =
    pathSegments[0] === OUTBOX_SEGMENTS[segment].sentTab
      ? 'deliveries'
      : pathSegments[0] === DRAFTS_TAB
        ? 'drafts'
        : null;
  if (tab == null) {
    return { kind: 'ember' };
  }
  if (rest.length === 0) {
    return { kind: 'list', tab };
  }
  if (tab === 'deliveries' && rest.length === 1) {
    return { kind: 'detail', tab, id: rest[0] };
  }
  return { kind: 'ember' };
}

type OutboxRouteViewProps = {
  segment: OutboxSegment;
  workspaceId: WorkspaceIdRouteParamInput;
  /** The path after `/journeys/<segment>/` (e.g. `deliveries`, `drafts`, `deliveries/<id>`). */
  splat: string;
};

/**
 * Gate + render for the Outbox subtree, across both taxonomy segments (`outbox` current,
 * `message_activity` future). Migrated to React: the Deliveries/Sent tab (list + a single
 * `:id` detail) and the Drafts tab (list only). The index redirect, the draft detail/edit,
 * and any deeper path fall through to the embedded Ember host — the same shape as the
 * kill-switch-off case.
 *
 * The segment redirect lives here (not in `beforeLoad`) on purpose: `beforeLoad` runs before
 * `accountStore` is populated on a cold load, so a flag read there races and would disagree
 * with the rendered title/nav. Reading the rename stage from `useProductTaxonomy` — the same
 * source as the tab labels — keeps the URL vocabulary and the visible copy consistent.
 */
export function OutboxRouteView({ segment, workspaceId: workspaceIdParam, splat }: OutboxRouteViewProps) {
  const isMigrationEnabled = useFeatureFlag(AccountFeatureFlag.OutboxMigration);
  const taxonomy = useProductTaxonomy();
  const router = useRouter();
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = normalizeWorkspaceIdRouteParam(workspaceIdParam);
  const canonicalSegment = SEGMENT_BY_VARIANT[taxonomy.variant];

  // Kill-switch off: the embedded Ember app serves this URL. Route ownership
  // (`route-ownership.ts`) falls back to `embedded` on the same flag, so ownership and the
  // rendered app agree.
  if (!isMigrationEnabled) {
    return <Journeys />;
  }

  // The migrated pages read the workspace via `requireWorkspaceId()` (a URL parse) that can't
  // resolve the `/workspaces/last` sentinel, so resolve it to the real id before they render.
  // Only on the migrated (Hydra-owned) path — the flag-off Ember host above keeps `last` as-is
  // and handles it itself; a no-op for numeric workspace URLs.
  if (workspaceId === 'last') {
    return currentWorkspace == null ? null : (
      <Navigate
        to={OUTBOX_SEGMENTS[segment].splatRoute}
        params={{ workspaceId: String(currentWorkspace.id), _splat: splat }}
        search
        replace
      />
    );
  }

  const view = classifyOutboxPath(segment, splat);
  if (view.kind === 'ember') {
    return <Journeys />;
  }

  // Forward the non-canonical taxonomy segment to the canonical one, preserving tab + detail id.
  if (segment !== canonicalSegment) {
    const detailRest = view.kind === 'detail' ? [view.id] : [];
    const canonicalSplat = [tabSegmentFor(canonicalSegment, view.tab), ...detailRest].join('/');
    return (
      <Navigate
        to={OUTBOX_SEGMENTS[canonicalSegment].splatRoute}
        params={{ workspaceId, _splat: canonicalSplat }}
        search
        replace
      />
    );
  }

  const splatHref = (targetSplat: string) =>
    router.buildLocation({
      to: OUTBOX_SEGMENTS[canonicalSegment].splatRoute,
      params: { workspaceId, _splat: targetSplat },
    }).href;

  if (view.kind === 'detail') {
    // Each related row links to its own delivery: a drafted sibling routes to the
    // Ember-owned draft path, everything else to the React detail (Ember picked
    // `draft.index` vs `outbox.deliveries.delivery` the same way).
    const relatedDeliveryHref = (related: Delivery) => {
      const tab = getDeliveryState(related) === 'drafted' ? DRAFTS_TAB : OUTBOX_SEGMENTS[canonicalSegment].sentTab;
      return splatHref(`${tab}/${related.id}`);
    };
    return (
      <AppErrorBoundary resetKeys={[view.id]}>
        <DeliveryDetailPage deliveryId={view.id} getDeliveryHref={relatedDeliveryHref} />
      </AppErrorBoundary>
    );
  }

  const tabHref = (target: OutboxTab) => splatHref(tabSegmentFor(canonicalSegment, target));
  const getDeliveryHref = (delivery: Delivery) =>
    splatHref(`${tabSegmentFor(canonicalSegment, view.tab)}/${delivery.id}`);

  return <OutboxPage tab={view.tab} tabHref={tabHref} getDeliveryHref={getDeliveryHref} />;
}
